import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const LeaveRequestForm = ({ leaveBalances, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    leaveType: 'annual',
    startDate: '',
    endDate: '',
    reason: ''
  });

  const [errors, setErrors] = useState({});

  const leaveTypeOptions = [
    { value: 'annual', label: 'Annual Leave' },
    { value: 'sick', label: 'Sick Leave' },
    { value: 'maternity', label: 'Maternity Leave' },
    { value: 'long_service', label: 'Long Service Leave' }
  ];

  const calculateDays = () => {
    if (!formData?.startDate || !formData?.endDate) return 0;
    const start = new Date(formData?.startDate);
    const end = new Date(formData?.endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const getAvailableBalance = () => {
    const balance = leaveBalances?.find(b => b?.leaveType === formData?.leaveType);
    return balance?.balanceDays || 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const newErrors = {};

    if (!formData?.startDate) newErrors.startDate = 'Start date is required';
    if (!formData?.endDate) newErrors.endDate = 'End date is required';
    if (!formData?.reason?.trim()) newErrors.reason = 'Reason is required';

    const daysRequested = calculateDays();
    const availableBalance = getAvailableBalance();

    if (daysRequested > availableBalance) {
      newErrors.balance = `Insufficient leave balance. Available: ${availableBalance} days`;
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit?.({
      ...formData,
      daysRequested
    });
  };

  const daysRequested = calculateDays();
  const availableBalance = getAvailableBalance();

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Leave Type
        </label>
        <select
          value={formData?.leaveType}
          onChange={(e) => setFormData({ ...formData, leaveType: e?.target?.value })}
          className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {leaveTypeOptions?.map(option => (
            <option key={option?.value} value={option?.value}>
              {option?.label}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Start Date
          </label>
          <Input
            type="date"
            value={formData?.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e?.target?.value })}
            error={errors?.startDate}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            End Date
          </label>
          <Input
            type="date"
            value={formData?.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e?.target?.value })}
            error={errors?.endDate}
          />
        </div>
      </div>
      {daysRequested > 0 && (
        <div className="bg-muted/30 border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Days Requested</p>
              <p className="text-lg font-semibold text-foreground">{daysRequested} days</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Available Balance</p>
              <p className={`text-lg font-semibold ${daysRequested > availableBalance ? 'text-error' : 'text-success'}`}>
                {availableBalance} days
              </p>
            </div>
          </div>
          {errors?.balance && (
            <p className="text-xs text-error mt-2">
              <Icon name="AlertTriangle" size={12} className="inline mr-1" />
              {errors?.balance}
            </p>
          )}
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Reason
        </label>
        <textarea
          value={formData?.reason}
          onChange={(e) => setFormData({ ...formData, reason: e?.target?.value })}
          rows={3}
          className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Please provide a reason for your leave request..."
        />
        {errors?.reason && (
          <p className="text-xs text-error mt-1">{errors?.reason}</p>
        )}
      </div>
      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          className="flex-1"
        >
          {loading ? 'Submitting...' : 'Submit Request'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default LeaveRequestForm;
